import SkeletonRow from '@/components/UI/molecules/tables/SkeletonRow';
import {
    TableCell,
    TableCellHead,
    TableCellHeadInitial,
    TableContainerBinnance,
    TableHeadRow,
} from '@/components/UI/molecules/tables/TablePrincipal';
import StatusTextGenecic from '@/components/UI/atoms/buttons/StatusTextGeneric';
import {ExpenseAccountValidationEnum, FeatureCodeEnum} from '@/enums/permissions.enum';
import {Can} from '@/context/AbilityContext';
import {ACLObj} from '@components/auth/guard/AclGuard';
import CustomPagination from '@components/UI/organisms/pagination/Pagination';
import {RootState} from '@config/store';
import {Box, Grid, IconButton, Tooltip, Typography, useTheme} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CodeIcon from '@mui/icons-material/Code';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import {DateTime} from 'luxon';
import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';

import {
    downloadInvoiceDocument,
    fetchExpenseAccountDetail,
    IExpenseAccountDetailHeader,
    IExpenseAccountDetailRow,
} from '@components/expenseAccountValidation/redux/actions/ExpenseAccountValidationActions';

/**
 * Anchos de las 14 columnas. **Suman 12**, que es la rejilla de MUI.
 *
 * Se declaran aquí y no repartidos por el JSX porque con 13 la tabla se desmaquetaba:
 * cada renglón desbordaba y empujaba al siguiente, dejando la tabla en escalera.
 */
const COLS = {
    cveImport: 0.7,
    supplierNumber: 0.8,
    supplierName: 1.2,
    invoiceReference: 0.8,
    invoiceDate: 0.8,
    description: 0.9,
    subtotal: 0.8,
    ivaRate: 0.5,
    ivaAmount: 0.8,
    retentionRate: 0.5,
    retentionAmount: 0.8,
    total: 0.8,
    status: 0.9,
    message: 0.8,
    documents: 0.9,
};

const formatDate = (value?: string | null): string =>
    value ? DateTime.fromISO(value).toFormat('dd/MM/yyyy') : '---';

/**
 * Detalle interno de una Cuenta de Gastos (PDPA-5).
 *
 * Es una visual propia del módulo de validación, no la del Agente Aduanal: consulta el
 * endpoint protegido por el permiso de este módulo y agrega lo que el revisor necesita y
 * el agente no tiene —la descarga documento por documento—.
 *
 * Muestra la versión vigente, que es lo que pide el criterio 3 de la HU del módulo.
 */
const ExpenseAccountValidationDetail: React.FC & {acl?: ACLObj} = () => {
    const {t} = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const {customsAgentCode, customRequest} = useParams();
    const token = useSelector((state: RootState) => state.logins.accessToken);

    const [rows, setRows] = useState<IExpenseAccountDetailRow[]>([]);
    const [header, setHeader] = useState<IExpenseAccountDetailHeader | undefined>();
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(25);
    const [totalPages, setTotalPages] = useState(0);
    const [downloading, setDownloading] = useState<string | null>(null);

    const load = useCallback(
        async (nextPage: number, nextLimit: number) => {
            setLoading(true);
            setFailed(false);
            try {
                // Los parámetros de la ruta son opcionales en el tipo de react-router;
                // aquí ya se comprobó que existen antes de llamar.
                const detalle = await fetchExpenseAccountDetail(
                    token,
                    customsAgentCode as string,
                    customRequest as string,
                    nextPage,
                    nextLimit
                );
                setRows(detalle.rows);
                setHeader(detalle.header);
                setTotalPages(detalle.totalPages);
            } catch (error) {
                setFailed(true);
                setRows([]);
            } finally {
                setLoading(false);
            }
        },
        [token, customsAgentCode, customRequest]
    );

    useEffect(() => {
        if (token && customsAgentCode && customRequest) load(page, limit);
    }, [token, customsAgentCode, customRequest, page, limit, load]);

    /**
     * Criterio 8. Se vuelve al listado sin tocar los filtros: el módulo los conserva en su
     * estado y los reaplica al montar, así que el revisor no pierde la búsqueda.
     */
    const goBack = () => navigate('/expense-accounts-validation');

    const download = async (documentId: string, fileName: string) => {
        setDownloading(documentId);
        try {
            await downloadInvoiceDocument(token, documentId, fileName);
        } catch (error) {
            window.alert(t('expense_account_validation.detail.download_error'));
        } finally {
            setDownloading(null);
        }
    };

    /** Criterio 3: una acción por documento, con su icono según el tipo. */
    const documentAction = (row: IExpenseAccountDetailRow, extension: 'xml' | 'pdf') => {
        const document = row.documents?.find((item) => item.extension?.toLowerCase() === extension);
        if (!document) return null;
        const Icon = extension === 'pdf' ? PictureAsPdfIcon : CodeIcon;
        return (
            <Tooltip
                title={`${t(
                    'expense_account_validation.detail.download'
                )} ${extension.toUpperCase()}`}
            >
                <span>
                    <IconButton
                        size="small"
                        disabled={downloading === document.id}
                        onClick={() => download(document.id, document.fileName)}
                    >
                        <Icon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
        );
    };

    const headerField = (label: string, value?: string | number | null) => (
        <Box sx={{marginRight: '28px'}}>
            <Typography sx={{fontSize: '10px', color: '#6C6C6C'}}>{label}</Typography>
            <Typography sx={{fontSize: '13px', fontWeight: 600}}>{value ?? '---'}</Typography>
        </Box>
    );

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%', padding: '15px 23px'}}>
            <Box sx={{display: 'flex', alignItems: 'center', padding: '10px 0'}}>
                <IconButton
                    onClick={goBack}
                    aria-label={t('expense_account_validation.detail.back')}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography
                    sx={{
                        fontSize: '24px',
                        fontFamily: theme.typography.fontFamily,
                        fontWeight: 400,
                    }}
                >
                    {t('expense_account_validation.detail.title')}
                </Typography>
            </Box>

            {/* Criterio 1 · Identifica sin ambigüedad qué Cuenta de Gastos se está viendo. */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    background: '#fff',
                    padding: '15px',
                    marginBottom: '10px',
                }}
            >
                {headerField(t('expense_account_validation.table.custom_request'), customRequest)}
                {headerField(t('expense_account_validation.table.folio'), header?.folio)}
                {headerField(t('expense_account_validation.table.custom_agent'), customsAgentCode)}
                {headerField(
                    t('expense_account_validation.table.uploaded_at'),
                    formatDate(header?.uploadedAt)
                )}
                <Box>
                    <Typography sx={{fontSize: '10px', color: '#6C6C6C'}}>
                        {t('expense_account_validation.table.status')}
                    </Typography>
                    {header?.status && (
                        <StatusTextGenecic
                            status={header.status}
                            text={t(`expense_account_validation.status.${header.status}`)}
                        />
                    )}
                </Box>
            </Box>

            <TableContainerBinnance container>
                <TableHeadRow container>
                    <Grid item xs={COLS.cveImport}>
                        <TableCellHeadInitial sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.detail.cve_import')}
                        </TableCellHeadInitial>
                    </Grid>
                    <Grid item xs={COLS.supplierNumber}>
                        <TableCellHead sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.detail.supplier_number')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.supplierName}>
                        <TableCellHead sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.detail.supplier_name')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.invoiceReference}>
                        <TableCellHead sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.detail.invoice_reference')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.invoiceDate}>
                        <TableCellHead sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.detail.invoice_date')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.description}>
                        <TableCellHead sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.detail.description')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.subtotal}>
                        <TableCellHead sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.detail.subtotal')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.ivaRate}>
                        <TableCellHead sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.detail.iva_rate')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.ivaAmount}>
                        <TableCellHead sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.detail.iva_amount')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.retentionRate}>
                        <TableCellHead sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.detail.retention_rate')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.retentionAmount}>
                        <TableCellHead sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.detail.retention_amount')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.total}>
                        <TableCellHead sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.detail.total')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.status}>
                        <TableCellHead sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.detail.status')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.message}>
                        <TableCellHead sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.detail.message')}
                        </TableCellHead>
                    </Grid>
                    <Grid item xs={COLS.documents}>
                        <TableCellHead sx={{fontSize: '9.5px'}}>
                            {t('expense_account_validation.detail.documents')}
                        </TableCellHead>
                    </Grid>
                </TableHeadRow>

                {loading ? (
                    <>
                        <SkeletonRow />
                        <SkeletonRow />
                        <SkeletonRow />
                    </>
                ) : (
                    rows.map((row, index) => (
                        <React.Fragment key={row.uuid ?? index}>
                            {/* Clave de importación de SAP (ZI17, ZL10...). No es un
                                monto pese al nombre de la columna; es la misma que muestra
                                el detalle de Agentes Aduanales. Cuando falta, el aviso ya
                                sale en la columna Mensaje, así que aquí no se repite. */}
                            <Grid item xs={COLS.cveImport} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>
                                    {row.importKeys ?? '---'}
                                </TableCell>
                            </Grid>
                            <Grid item xs={COLS.supplierNumber} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>
                                    {row.supplierNumbers ?? '---'}
                                </TableCell>
                            </Grid>
                            <Grid item xs={COLS.supplierName} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                                        {row.supplierName ?? '---'}
                                        {/* Criterio 4 · La de honorarios del Agente Aduanal
                                            se distingue de las de proveedores. */}
                                        {row.isFeeInvoice && (
                                            <Tooltip
                                                title={t(
                                                    'expense_account_validation.detail.fee_invoice'
                                                )}
                                            >
                                                <WorkspacePremiumOutlinedIcon
                                                    fontSize="small"
                                                    color="primary"
                                                />
                                            </Tooltip>
                                        )}
                                    </Box>
                                </TableCell>
                            </Grid>
                            <Grid item xs={COLS.invoiceReference} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>
                                    {row.invoiceNumber ?? '---'}
                                </TableCell>
                            </Grid>
                            <Grid item xs={COLS.invoiceDate} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>
                                    {formatDate(row.invoiceDate)}
                                </TableCell>
                            </Grid>
                            <Grid item xs={COLS.description} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>
                                    {row.descriptions ?? '---'}
                                </TableCell>
                            </Grid>
                            <Grid item xs={COLS.subtotal} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>
                                    {row.amounts ?? '---'}
                                </TableCell>
                            </Grid>
                            <Grid item xs={COLS.ivaRate} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>
                                    {row.taxesIva ?? '---'}
                                </TableCell>
                            </Grid>
                            <Grid item xs={COLS.ivaAmount} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>
                                    {row.ivaAmounts ?? '---'}
                                </TableCell>
                            </Grid>
                            <Grid item xs={COLS.retentionRate} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>
                                    {row.retentionFactors ?? '---'}
                                </TableCell>
                            </Grid>
                            <Grid item xs={COLS.retentionAmount} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>
                                    {row.retentions ?? '---'}
                                </TableCell>
                            </Grid>
                            <Grid item xs={COLS.total} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>
                                    {row.grossAmounts ?? '---'}
                                </TableCell>
                            </Grid>
                            <Grid item xs={COLS.status} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>
                                    {row.status ? (
                                        <StatusTextGenecic
                                            status={row.status}
                                            text={t(`poagents.status.${row.status}`)}
                                        />
                                    ) : (
                                        '---'
                                    )}
                                </TableCell>
                            </Grid>
                            <Grid item xs={COLS.message} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                                        {/* Criterio 5 · La factura sin clave SAP se queda en
                                            la Cuenta de Gastos, marcada, y no se envía. */}
                                        {row.inconsistency && (
                                            <Tooltip
                                                title={t(
                                                    `poagents.inconsistency.${row.inconsistency}`
                                                )}
                                            >
                                                <ReportProblemOutlinedIcon
                                                    fontSize="small"
                                                    color="error"
                                                />
                                            </Tooltip>
                                        )}
                                        {row.messages || (row.inconsistency ? '' : '---')}
                                    </Box>
                                </TableCell>
                            </Grid>
                            <Grid item xs={COLS.documents} display="grid">
                                <TableCell sx={{fontSize: '9.5px'}}>
                                    <Can
                                        I={ExpenseAccountValidationEnum.DOWNLOAD_ZIP}
                                        a={FeatureCodeEnum.EXPENSE_ACCOUNT_VALIDATION}
                                    >
                                        {documentAction(row, 'xml')}
                                        {documentAction(row, 'pdf')}
                                        {/* Los expedientes cargados antes de que se
                                            conservaran los archivos no tienen documentos. */}
                                        {!row.documents?.length && '---'}
                                    </Can>
                                </TableCell>
                            </Grid>
                        </React.Fragment>
                    ))
                )}
            </TableContainerBinnance>

            {failed && (
                <Typography sx={{fontSize: '12px', padding: '20px', textAlign: 'center'}}>
                    {t('expense_account_validation.detail.load_error')}
                </Typography>
            )}

            <CustomPagination
                totalPages={totalPages}
                currentPage={page}
                handleChange={(_event: React.ChangeEvent<unknown>, value: number) => setPage(value)}
                limit={limit}
                handleChangeRowsPerPage={(event: any) => {
                    setLimit(Number(event.target.value));
                    setPage(1);
                }}
            />
        </Box>
    );
};

ExpenseAccountValidationDetail.acl = {
    action: ExpenseAccountValidationEnum.VIEW_DETAIL,
    subject: FeatureCodeEnum.EXPENSE_ACCOUNT_VALIDATION,
};

export default ExpenseAccountValidationDetail;
